import { Container, Graphics, NineSliceSprite, Text } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import { woodGradient } from './BoardPanel';
import { COLOURS, displayStyle } from './ui';

export type BrassButtonKind = 'gold' | 'wood';

/**
 * A brass-edged button: gold for the action that pays or plays, wood for
 * everything else (guesses, cancel). Tap with a press-down; disabled
 * buttons dim and ignore taps.
 */
export class BrassButton extends Container {
  private readonly face = new Graphics();
  private readonly caption: Text;
  private w: number;
  private h: number;
  private enabled = true;
  onTap: (() => void) | null;

  constructor(options: { label: string; kind?: BrassButtonKind; onTap?: () => void; width?: number; height?: number; size?: number }) {
    super();
    this.kind = options.kind ?? 'gold';
    this.w = options.width ?? 150;
    this.h = options.height ?? 48;
    this.onTap = options.onTap ?? null;
    this.caption = new Text({
      text: options.label,
      style: {
        ...displayStyle(options.size ?? 18, this.kind === 'gold' ? 0x3a1a00 : COLOURS.cream),
        ...(this.kind === 'gold' ? { stroke: undefined, dropShadow: undefined } : {}),
      },
    });
    this.caption.anchor.set(0.5);
    this.addChild(this.face, this.caption);
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.on('pointerdown', () => {
      if (!this.enabled) return;
      this.face.y = 2;
      this.caption.y = this.h / 2 + 2;
    });
    const up = () => {
      this.face.y = 0;
      this.caption.y = this.h / 2;
    };
    this.on('pointerup', up);
    this.on('pointerupoutside', up);
    this.on('pointertap', () => {
      if (this.enabled) this.onTap?.();
    });
    this.redraw();
  }

  readonly kind: BrassButtonKind;

  get labelText(): string {
    return this.caption.text;
  }

  setLabel(text: string): void {
    this.caption.text = text;
    this.fitCaption();
  }

  /** The caption's width at its full size, for sizing a row to its words. */
  get captionWidth(): number {
    return this.caption.width / (this.caption.scale.x || 1);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.alpha = enabled ? 1 : 0.5;
    this.cursor = enabled ? 'pointer' : 'default';
  }

  resize(width: number, height = this.h): void {
    this.w = width;
    this.h = height;
    this.redraw();
  }

  get buttonWidth(): number {
    return this.w;
  }

  get buttonHeight(): number {
    return this.h;
  }

  private redraw(): void {
    const { w, h } = this;
    this.caption.x = w / 2;
    this.caption.y = h / 2;
    this.fitCaption();
    this.hitArea = { contains: (x: number, y: number) => x >= 0 && y >= 0 && x <= w && y <= h + 3 };
    if (this.paintPlate()) return;
    const fill =
      this.kind === 'gold' ? woodGradient(0xffe08a, 0xe0a020) : woodGradient(0x7a4a1c, 0x4a2a0c);
    this.face
      .clear()
      .roundRect(0, 3, w, h, 12)
      .fill({ color: 0x5a3210 })
      .roundRect(0, 0, w, h, 12)
      .fill(fill)
      .stroke({ color: 0xd9a441, width: 2 })
      .roundRect(3, 2, w - 6, h * 0.42, 9)
      .fill({ color: 0xffffff, alpha: 0.14 });
  }

  /**
   * Draw the painted plate (ui_button/plate_gold or plate_wood) when it has
   * loaded: a nine-slice, so its rounded ends keep their shape at any width.
   * False while it hasn't, and the drawn face stands in.
   */
  private paintPlate(): boolean {
    const texture = AssetLoader.shared.getTexture(`shared:button:plate_${this.kind}`);
    if (!texture) return false;
    if (!this.plate || this.plate.texture !== texture) {
      this.plate?.destroy();
      this.plate = new NineSliceSprite({ texture, ...PLATE_SLICE });
      this.face.clear();
      this.face.addChild(this.plate);
    }
    // The slices keep the art's proportions: scale the whole plate to the
    // button's height and stretch only its middle.
    const k = this.h / texture.height;
    this.plate.scale.set(k);
    this.plate.width = Math.max(PLATE_SLICE.leftWidth + PLATE_SLICE.rightWidth, this.w / k);
    this.plate.height = texture.height;
    return true;
  }

  private plate: NineSliceSprite | null = null;

  /** Keep the words clear of the plate's rounded ends: a caption too long
   *  for its plate shrinks to fit (never below 80%); one that fits is left
   *  at full size. */
  private fitCaption(): void {
    const room = Math.max(1, this.w - captionInset(this.h) * 2);
    this.caption.scale.set(Math.max(0.8, Math.min(1, room / Math.max(1, this.captionWidth))));
  }
}

/** How far a caption keeps from each end of a plate `height` tall. */
export const captionInset = (height: number): number => Math.round(height * 0.32);

/** The plates' ends and bevels, in the art's pixels (150 tall). */
const PLATE_SLICE = { leftWidth: 72, rightWidth: 72, topHeight: 58, bottomHeight: 58 };

/**
 * A painted plate on its own (for a label or chip that isn't a button), at
 * `width` x `height`; null until the art has loaded.
 */
export function paintedPlate(kind: BrassButtonKind, width: number, height: number): NineSliceSprite | null {
  const texture = AssetLoader.shared.getTexture(`shared:button:plate_${kind}`);
  if (!texture) return null;
  const plate = new NineSliceSprite({ texture, ...PLATE_SLICE });
  const k = height / texture.height;
  plate.scale.set(k);
  plate.width = Math.max(PLATE_SLICE.leftWidth + PLATE_SLICE.rightWidth, width / k);
  plate.height = texture.height;
  return plate;
}

/**
 * Lay out a dialog's buttons along one row: cancel on the left, the action
 * on the right (producer 2026-10-14). With no cancel the action takes the
 * whole row.
 */
export function layoutButtonRow(
  cancel: BrassButton | null,
  action: BrassButton,
  x: number,
  y: number,
  width: number,
  gap = 10,
): void {
  if (!cancel) {
    action.resize(width);
    action.position.set(x, y);
    return;
  }
  const each = Math.floor((width - gap) / 2);
  cancel.resize(each);
  action.resize(width - gap - each);
  cancel.position.set(x, y);
  action.position.set(x + each + gap, y);
}
